import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CLoader, SEO } from '../../components/common';
import {
  IoArrowBack, IoBusinessOutline, IoDocumentTextOutline,
  IoStatsChartOutline, IoPeopleOutline, IoShieldCheckmarkOutline,
} from 'react-icons/io5';
import { FiExternalLink, FiTrendingUp, FiTrendingDown, FiUsers, FiCheckCircle, FiAlertTriangle, FiSend, FiX } from 'react-icons/fi';
import { getCompanyDetails, CompanyDetails, getCompanyRatiosHistory, CompanyRatioPoint } from '../../api/copilot';
import { ShareholdingChart, GrowthMetrics, SegmentCharts, StockPriceChart } from './CompanyCharts';
import { ValuationMetrics } from './ValuationMetrics';
import { PeerComparison } from './PeerComparison';
import GovernanceTab from './GovernanceTab';

type TabType = 'overview' | 'price' | 'financials' | 'peers' | 'management' | 'governance' | 'documents';

const CompanyDetail: React.FC = () => {
  const { companyName } = useParams<{ companyName: string }>();
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotQuery, setCopilotQuery] = useState('');
  const [ratiosHistory, setRatiosHistory] = useState<CompanyRatioPoint[]>([]);
  const copilotRef = useRef<HTMLDivElement>(null);

  const decodedCompanyName = companyName ? decodeURIComponent(companyName) : '';

  useEffect(() => {
    const loadCompanyDetails = async () => {
      if (!decodedCompanyName) return;
      try {
        setLoading(true);
        setError(null);
        const result = await getCompanyDetails(decodedCompanyName);
        if (result.status === 'success' && result.data) {
          setCompany(result.data);
        } else {
          setError(result.error || 'Company not found');
        }
      } catch (err) {
        console.error('Failed to fetch company details:', err);
        setError('Failed to load company data');
      } finally {
        setLoading(false);
      }
    };
    loadCompanyDetails();
  }, [decodedCompanyName]);

  // ── Fetch historical ratios ──
  useEffect(() => {
    if (company?.ticker_symbol) {
      getCompanyRatiosHistory(company.ticker_symbol)
        .then(setRatiosHistory)
        .catch(() => setRatiosHistory([]));
    }
  }, [company?.ticker_symbol]);

  // ── Close copilot on outside click ──
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (copilotRef.current && !copilotRef.current.contains(event.target as Node)) {
        setCopilotOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Close copilot on Escape ──
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCopilotOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // ---------- Helpers ----------

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '-') return '-';
    if (typeof value === 'number') {
      if (value >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
      if (value >= 100000) return `${(value / 100000).toFixed(2)} L`;
      return value.toLocaleString();
    }
    return String(value);
  };

  const parseHighLow = (hl: string | undefined): { high: number; low: number } | null => {
    if (!hl) return null;
    const parts = hl.split('/').map(s => parseFloat(s.trim().replace(/,/g, '')));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      const high = Math.max(parts[0], parts[1]);
      const low = Math.min(parts[0], parts[1]);
      return { high, low };
    }
    return null;
  };

  const getChangeColor = (val: number | null | undefined) => {
    if (!val) return colors.textColor;
    return val > 0 ? '#22c55e' : val < 0 ? '#ef4444' : colors.textColor;
  };

  const formatPercent = (val: number | null | undefined) => {
    if (val === null || val === undefined) return '-';
    const sign = val > 0 ? '+' : '';
    return `${sign}${val.toFixed(1)}%`;
  };

  // ---------- Copilot ----------

  const companyDisplayName = company?.name || decodedCompanyName;

  const copilotNudges = [
    { icon: '📐', label: 'Reverse DCF', query: `What growth rate is priced into ${companyDisplayName} at current price? Do a reverse DCF analysis.` },
    { icon: '🔬', label: 'Deep Analysis', query: `Deep-dive into ${companyDisplayName}'s fundamentals - profitability, capital efficiency, and balance sheet.` },
    { icon: '⚖️', label: 'Peer Comparison', query: `Compare ${companyDisplayName} with its top peers on valuation, growth, and profitability.` },
    { icon: '⚠️', label: 'Risk Assessment', query: `What are the key risks for ${companyDisplayName}? Business, financial, and regulatory risks.` },
    { icon: '📊', label: 'Earnings Analysis', query: `Analyze ${companyDisplayName}'s latest quarterly results. Revenue, margins, and profit trends.` },
    { icon: '🔮', label: 'Growth Outlook', query: `What is the growth outlook for ${companyDisplayName}? Where will future revenue come from?` },
    { icon: '💰', label: 'Valuation Check', query: `Is ${companyDisplayName} overvalued or undervalued? Compare valuation multiples with historical averages and peers.` },
  ];

  const handleCopilotSend = (query: string) => {
    if (!query.trim()) return;
    setCopilotOpen(false);
    setCopilotQuery('');
    navigate(`/copilot?q=${encodeURIComponent(query.trim())}`);
  };

  // ---------- Tab Config ----------

  const hasPeerData = Array.isArray(company?.peer_comparison) &&
    company!.peer_comparison!.length > 0 &&
    company!.peer_comparison![0] != null &&
    typeof company!.peer_comparison![0] === 'object' &&
    !Array.isArray(company!.peer_comparison![0]);

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: IoBusinessOutline, show: true },
    { id: 'price' as TabType, label: 'Price', icon: FiTrendingUp, show: !!company?.ticker_symbol },
    { id: 'financials' as TabType, label: 'Financials', icon: IoStatsChartOutline, show: true },
    { id: 'peers' as TabType, label: 'Peers', icon: FiUsers, show: hasPeerData },
    { id: 'management' as TabType, label: 'Management', icon: IoPeopleOutline, show: true },
    { id: 'governance' as TabType, label: 'Governance', icon: IoShieldCheckmarkOutline, show: true },
    { id: 'documents' as TabType, label: 'Documents', icon: IoDocumentTextOutline, show: true },
  ].filter(t => t.show);

  // ---------- Header with Price & 52-Week Range ----------

  const renderHeader = () => {
    if (!company) return null;
    const range = parseHighLow(company.high_low);
    const currentPrice = typeof company.current_price === 'number' ? company.current_price : parseFloat(String(company.current_price || '0').replace(/,/g, ''));
    const qm = company.quarterly_metrics;

    // Calculate position in 52-week range
    let rangePercent = 50;
    if (range && range.high !== range.low) {
      rangePercent = Math.max(0, Math.min(100, ((currentPrice - range.low) / (range.high - range.low)) * 100));
    }

    return (
      <div className="mb-4">
        {/* Company Name + Badges */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full transition-colors flex-shrink-0"
            style={{ backgroundColor: colors.inputBackground }}
          >
            {React.createElement(IoArrowBack as any, { size: 24, color: colors.textColor })}
          </button>
          <div className="flex-1 min-w-0">
            <CText type="B20" color={colors.textColor}>{company.name}</CText>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {company.ticker_symbol && (
                <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: colors.primary, color: colors.white }}>
                  {company.ticker_symbol}
                </span>
              )}
              {company.sector && (
                <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: colors.inputBackground, color: colors.grayScale500 }}>
                  {company.sector}
                </span>
              )}
              {company.industry && (
                <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: colors.inputBackground, color: colors.grayScale400 }}>
                  {company.industry}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {company.nse_link && (
              <a href={company.nse_link} target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ backgroundColor: colors.primary, color: colors.white }}>
                NSE
              </a>
            )}
            {company.bse_link && (
              <a href={company.bse_link} target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ backgroundColor: colors.inputBackground, color: colors.textColor, border: `1px solid ${colors.borderColor}` }}>
                BSE
              </a>
            )}
          </div>
        </div>

        {/* Price Card with 52-Week Range */}
        <div
          className="p-4 rounded-xl mb-4"
          style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <CText type="B24" color={colors.textColor}>₹{currentPrice.toLocaleString()}</CText>
              {qm && (
                <div className="flex items-center gap-3 mt-1">
                  {qm.sales_yoy != null && (
                    <span className="flex items-center gap-1">
                      <CText type="r12" color={colors.grayScale400}>Sales YoY:</CText>
                      <CText type="M12" color={getChangeColor(qm.sales_yoy)}>{formatPercent(qm.sales_yoy)}</CText>
                    </span>
                  )}
                  {qm.np_yoy != null && (
                    <span className="flex items-center gap-1">
                      <CText type="r12" color={colors.grayScale400}>Profit YoY:</CText>
                      <CText type="M12" color={getChangeColor(qm.np_yoy)}>{formatPercent(qm.np_yoy)}</CText>
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="text-right">
              <CText type="r12" color={colors.grayScale400}>Market Cap</CText>
              <CText type="B16" color={colors.textColor}>{formatValue(company.market_cap)}</CText>
            </div>
          </div>

          {/* 52-Week Range Bar */}
          {range && (
            <div className="mt-3">
              <div className="flex justify-between mb-1">
                <CText type="r10" color={colors.grayScale400}>52W Low: ₹{range.low.toLocaleString()}</CText>
                <CText type="r10" color={colors.grayScale400}>52W High: ₹{range.high.toLocaleString()}</CText>
              </div>
              <div className="relative h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.borderColor }}>
                {/* Gradient bar */}
                <div className="absolute inset-0 rounded-full" style={{
                  background: 'linear-gradient(90deg, #ef4444 0%, #f59e0b 30%, #22c55e 70%, #22c55e 100%)',
                  opacity: 0.3,
                }} />
                {/* Position marker */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-md"
                  style={{
                    left: `calc(${rangePercent}% - 6px)`,
                    backgroundColor: colors.primary,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ---------- Key Metrics ----------

  const renderMetricsCard = () => {
    if (!company) return null;

    const metrics = [
      { label: 'P/E Ratio', value: formatValue(company.price_to_earnings) },
      { label: 'Book Value', value: formatValue(company.book_value), prefix: '₹' },
      { label: 'ROE', value: company.roe ? `${Number(company.roe).toFixed(2)}%` : '-' },
      { label: 'ROCE', value: company.roce ? `${Number(company.roce).toFixed(2)}%` : '-' },
      { label: 'Dividend Yield', value: company.dividend_yield ? `${Number(company.dividend_yield).toFixed(2)}%` : '-' },
      { label: 'Face Value', value: formatValue(company.face_value), prefix: '₹' },
      { label: 'MCap/Sales', value: company.market_cap_to_sales ? Number(company.market_cap_to_sales).toFixed(2) : '-' },
      { label: 'EV/EBITDA', value: company.ev_to_ebitda ? Number(company.ev_to_ebitda).toFixed(2) : '-' },
      { label: 'P/B Ratio', value: company.price_to_book ? Number(company.price_to_book).toFixed(2) : '-' },
    ];

    return (
      <div
        className="p-4 rounded-xl mb-4"
        style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
      >
        <CText type="M14" color={colors.grayScale500} style={{ marginBottom: 12 }}>Key Metrics</CText>
        <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {metrics.map((metric) => (
            <div key={metric.label}>
              <CText type="r10" color={colors.grayScale400}>{metric.label}</CText>
              <CText type="B14" color={colors.textColor}>
                {metric.prefix || ''}{metric.value}
              </CText>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---------- Quarterly Metrics ----------

  const renderQuarterlyMetrics = () => {
    const qm = company?.quarterly_metrics;
    if (!qm) return null;

    const items = [
      { label: 'Sales YoY', value: qm.sales_yoy },
      { label: 'Sales QoQ', value: qm.sales_qoq },
      { label: 'Profit YoY', value: qm.np_yoy },
      { label: 'Profit QoQ', value: qm.np_qoq },
      { label: 'EBITDA YoY', value: qm.ebitda_yoy },
      { label: 'EBITDA QoQ', value: qm.ebitda_qoq },
    ].filter(item => item.value != null);

    if (items.length === 0) return null;

    return (
      <div
        className="p-4 rounded-xl mb-4"
        style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
      >
        <CText type="M14" color={colors.grayScale500} style={{ marginBottom: 12 }}>Quarterly Performance</CText>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {items.map((item) => (
            <div key={item.label} className="text-center">
              <CText type="r10" color={colors.grayScale400}>{item.label}</CText>
              <div className="flex items-center justify-center gap-1 mt-1">
                {React.createElement(
                  (item.value! > 0 ? FiTrendingUp : FiTrendingDown) as any,
                  { size: 12, color: getChangeColor(item.value) }
                )}
                <CText type="B14" color={getChangeColor(item.value)}>
                  {formatPercent(item.value)}
                </CText>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---------- Tab Content Renders ----------

  const renderInsightSection = (title: string, content: string | undefined) => {
    if (!content) return null;
    return (
      <div className="mb-4">
        <CText type="M14" color={colors.primary} style={{ marginBottom: 8 }}>{title}</CText>
        <div className="p-3 rounded-lg" style={{ backgroundColor: colors.backgroundColor, border: `1px solid ${colors.borderColor}` }}>
          <CText type="r14" color={colors.textColor} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{content}</CText>
        </div>
      </div>
    );
  };

  const renderOverviewTab = () => {
    const insights = company?.company_insights;
    const analysis = company?.company_analysis;

    return (
      <div>
        {/* Pros & Cons */}
        {analysis && (analysis.pros?.length || analysis.cons?.length) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {analysis.pros && analysis.pros.length > 0 && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#22c55e08', border: '1px solid #22c55e30' }}>
                <div className="flex items-center gap-2 mb-2">
                  {React.createElement(FiCheckCircle as any, { size: 14, color: '#22c55e' })}
                  <CText type="M14" color="#22c55e">Strengths</CText>
                </div>
                <ul className="space-y-1.5">
                  {analysis.pros.map((pro, i) => (
                    <li key={i} className="flex gap-2">
                      <span style={{ color: '#22c55e' }}>+</span>
                      <CText type="r12" color={colors.textColor} style={{ lineHeight: 1.5 }}>{pro}</CText>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {analysis.cons && analysis.cons.length > 0 && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#ef444408', border: '1px solid #ef444430' }}>
                <div className="flex items-center gap-2 mb-2">
                  {React.createElement(FiAlertTriangle as any, { size: 14, color: '#ef4444' })}
                  <CText type="M14" color="#ef4444">Concerns</CText>
                </div>
                <ul className="space-y-1.5">
                  {analysis.cons.map((con, i) => (
                    <li key={i} className="flex gap-2">
                      <span style={{ color: '#ef4444' }}>-</span>
                      <CText type="r12" color={colors.textColor} style={{ lineHeight: 1.5 }}>{con}</CText>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : null}

        {/* Business Insights */}
        {insights ? (
          <>
            {renderInsightSection('Business Overview', insights.business_overview)}
            {renderInsightSection('Product Segments', insights.product_segments)}
            {renderInsightSection('Future Plans', insights.future_plans)}
            {renderInsightSection('Clientele', insights.clientele)}
            {renderInsightSection('Suppliers', insights.suppliers)}
            {renderInsightSection('Joint Ventures', insights.joint_ventures)}
            {renderInsightSection('Subsidiaries', insights.subsidiaries)}
          </>
        ) : (
          <div className="text-center py-8">
            <CText type="M14" color={colors.grayScale500}>No business insights available for this company</CText>
          </div>
        )}
      </div>
    );
  };

  const renderFinancialTable = (title: string, data: any) => {
    if (!data) return null;
    let parsedData = data;
    if (typeof data === 'string') {
      try { parsedData = JSON.parse(data); } catch { return null; }
    }
    if (!Array.isArray(parsedData) || parsedData.length === 0) return null;
    const headers = Object.keys(parsedData[0]);

    return (
      <div className="mb-6">
        <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>{title}</CText>
        <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${colors.borderColor}` }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: colors.primary }}>
                {headers.map((header, i) => (
                  <th
                    key={header}
                    className={`px-3 py-2 text-left text-white font-medium whitespace-nowrap ${i === 0 ? 'sticky left-0 z-10' : ''}`}
                    style={i === 0 ? { backgroundColor: colors.primary } : undefined}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsedData.map((row: any, idx: number) => (
                <tr
                  key={idx}
                  style={{
                    backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground,
                    borderBottom: `1px solid ${colors.borderColor}`,
                  }}
                >
                  {headers.map((header, i) => (
                    <td
                      key={header}
                      className={`px-3 py-2 whitespace-nowrap ${i === 0 ? 'sticky left-0 z-10' : ''}`}
                      style={i === 0 ? { backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground } : undefined}
                    >
                      <CText type="r12" color={colors.textColor}>{formatValue(row[header])}</CText>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderFinancialsTab = () => {
    if (!company) return null;

    // Extract peer company names from peer_comparison data
    const peerRows = (company.peer_comparison || [])
      .filter((row): row is Record<string, string> => row != null && typeof row === 'object' && !Array.isArray(row));
    const peerNameKey = peerRows.length > 0
      ? Object.keys(peerRows[0]).find(k => k.toLowerCase().includes('name')) || Object.keys(peerRows[0])[0]
      : '';
    const peerNames = peerRows
      .map(row => row[peerNameKey])
      .filter((name): name is string => !!name && name !== company.name);

    return (
      <div>
        {/* Unified Valuation & Metrics */}
        <ValuationMetrics
          primaryName={company.name}
          primaryTicker={company.ticker_symbol || ''}
          ratiosHistory={ratiosHistory}
          yearlyResults={company.yearly_results_data}
          quarterlyResults={company.quarterly_results_data}
          balanceSheets={company.balance_sheets}
          currentRatios={{
            pe: company.price_to_earnings,
            pb: company.price_to_book,
            mcapSales: company.market_cap_to_sales,
            evEbitda: company.ev_to_ebitda,
          }}
          peerNames={peerNames}
          colors={colors}
        />

        {/* Shareholding */}
        {company.shareholding_ratios && company.shareholding_ratios.length > 0 && (
          <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
            <ShareholdingChart shareholdingRatios={company.shareholding_ratios} colors={colors} />
          </div>
        )}

        {/* Segment Charts */}
        {(company.product_segments || company.geographic_segments) && (
          <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
            <SegmentCharts
              productSegments={company.product_segments}
              geographicSegments={company.geographic_segments}
              colors={colors}
            />
          </div>
        )}

        {renderFinancialTable('Quarterly Results', company.quarterly_results_data)}
        {renderFinancialTable('Yearly Results', company.yearly_results_data)}
        {renderFinancialTable('Balance Sheet', company.balance_sheets)}
        {renderFinancialTable('Cash Flow', company.cashflow_statements)}
        {renderFinancialTable('Ratios', company.ratios)}
        {!company.quarterly_results_data && !company.yearly_results_data && !company.balance_sheets && !company.cashflow_statements && (
          <div className="text-center py-8">
            <CText type="M14" color={colors.grayScale500}>No financial data available</CText>
          </div>
        )}
      </div>
    );
  };

  const renderPeersTab = () => {
    if (!company) return null;
    return (
      <PeerComparison
        peerData={company.peer_comparison}
        currentCompany={company.name}
        colors={colors}
      />
    );
  };

  const renderManagementTab = () => {
    const insights = company?.company_insights;
    if (!insights) {
      return (
        <div className="text-center py-8">
          <CText type="M14" color={colors.grayScale500}>No management information available</CText>
        </div>
      );
    }
    return (
      <div>
        {renderInsightSection('Key Management Changes', insights.key_management_people_changes)}
        {renderInsightSection('Management Guidance', insights.management_guidance)}
        {renderInsightSection('Related Party Transactions', insights.related_party_transactions)}
      </div>
    );
  };

  const renderDocumentLink = (doc: any, idx: number) => {
    const url = doc.url || doc.pdf_url || doc.link ||
      (doc.resources && doc.resources.length > 0 ? doc.resources[0].link : null);
    const title = doc.title || doc.name || doc.report_name || `Document ${idx + 1}`;
    const date = doc.date || doc.report_date || doc.year;
    if (!url) return null;

    return (
      <a
        key={idx}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between p-3 rounded-lg mb-2 transition-colors hover:opacity-80"
        style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
      >
        <div className="flex items-center gap-3">
          {React.createElement(IoDocumentTextOutline as any, { size: 20, color: colors.primary })}
          <div>
            <CText type="M14" color={colors.textColor}>{title}</CText>
            {date && <CText type="r12" color={colors.grayScale400}>{date}</CText>}
          </div>
        </div>
        {React.createElement(FiExternalLink as any, { size: 16, color: colors.grayScale400 })}
      </a>
    );
  };

  const renderDocumentsTab = () => {
    if (!company) return null;
    const annualReports = Array.isArray(company.annual_reports_data) ? company.annual_reports_data : [];
    const concalls = Array.isArray(company.concalls_data) ? company.concalls_data : [];
    const announcements = Array.isArray(company.company_announcements_data) ? company.company_announcements_data : [];
    const hasDocuments = annualReports.length > 0 || concalls.length > 0 || announcements.length > 0;

    if (!hasDocuments) {
      return (
        <div className="text-center py-8">
          <CText type="M14" color={colors.grayScale500}>No documents available</CText>
        </div>
      );
    }

    return (
      <div>
        {annualReports.length > 0 && (
          <div className="mb-6">
            <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>Annual Reports</CText>
            {annualReports.map((doc, idx) => renderDocumentLink(doc, idx))}
          </div>
        )}
        {concalls.length > 0 && (
          <div className="mb-6">
            <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>Concall Transcripts</CText>
            {concalls.map((doc, idx) => renderDocumentLink(doc, idx))}
          </div>
        )}
        {announcements.length > 0 && (
          <div className="mb-6">
            <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>Company Announcements</CText>
            {announcements.map((doc, idx) => renderDocumentLink(doc, idx))}
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverviewTab();
      case 'price': return company?.ticker_symbol ? <StockPriceChart tickerSymbol={company.ticker_symbol} colors={colors} /> : null;
      case 'financials': return renderFinancialsTab();
      case 'peers': return renderPeersTab();
      case 'management': return renderManagementTab();
      case 'governance': return <GovernanceTab governance={company?.corporate_governance} companyInsights={company?.company_insights} colors={colors} />;
      case 'documents': return renderDocumentsTab();
      default: return null;
    }
  };

  // ---------- Main Render ----------

  return (
    <div className="p-4 lg:p-8 min-h-screen pb-8" style={{ backgroundColor: colors.backgroundColor }}>
      <SEO
        title={`${company?.name || decodedCompanyName}${company?.ticker_symbol ? ` (${company.ticker_symbol})` : ''} - Stock Analysis`}
        description={`Detailed analysis of ${company?.name || decodedCompanyName}.${company?.sector ? ` Sector: ${company.sector}.` : ''}${company?.industry ? ` Industry: ${company.industry}.` : ''} View financials, management insights, and documents on Yantrai.`}
        canonical={`/company/${encodeURIComponent(decodedCompanyName)}`}
        jsonLd={company ? {
          '@context': 'https://schema.org',
          '@type': 'Corporation',
          name: company.name,
          ...(company.ticker_symbol && { tickerSymbol: company.ticker_symbol }),
          ...(company.sector && { industry: company.sector }),
          ...(company.nse_link && { url: company.nse_link }),
        } : undefined}
      />
      <div className="lg:max-w-6xl lg:mx-auto">
        {/* Loading */}
        {loading && (
          <>
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => navigate(-1)} className="p-2 rounded-full" style={{ backgroundColor: colors.inputBackground }}>
                {React.createElement(IoArrowBack as any, { size: 24, color: colors.textColor })}
              </button>
              <CText type="B20" color={colors.textColor}>Loading...</CText>
            </div>
            <div className="flex justify-center items-center py-12"><CLoader /></div>
          </>
        )}

        {/* Error */}
        {error && !loading && (
          <>
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => navigate(-1)} className="p-2 rounded-full" style={{ backgroundColor: colors.inputBackground }}>
                {React.createElement(IoArrowBack as any, { size: 24, color: colors.textColor })}
              </button>
              <CText type="B20" color={colors.textColor}>{decodedCompanyName}</CText>
            </div>
            <div className="text-center py-8">
              <CText type="M14" color={colors.alertColor}>{error}</CText>
              <button className="mt-4 px-4 py-2 rounded-lg" style={{ backgroundColor: colors.primary, color: colors.white }}
                onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          </>
        )}

        {/* Content */}
        {!loading && !error && company && (
          <>
            {renderHeader()}
            {renderMetricsCard()}
            {renderQuarterlyMetrics()}

            {/* Growth Metrics */}
            <GrowthMetrics
              salesGrowth={company.sales_growth}
              profitGrowth={company.profit_growth}
              stockPriceCagr={company.stock_price_cagr}
              roeGrowth={company.roe_growth}
              yearlyResults={company.yearly_results_data}
              colors={colors}
            />

            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-colors"
                  style={{
                    backgroundColor: activeTab === tab.id ? colors.primary : colors.inputBackground,
                    color: activeTab === tab.id ? colors.white : colors.grayScale400,
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {React.createElement(tab.icon as any, { size: 16 })}
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
              {renderTabContent()}
            </div>
          </>
        )}
      </div>

      {/* ═══════════════ Copilot Widget (Right-middle) ═══════════════ */}
      {!loading && !error && company && (
        <div
          ref={copilotRef}
          className="fixed z-50 hidden md:block"
          style={{ right: 0, top: '50%', transform: 'translateY(-50%)' }}
        >
          {!copilotOpen ? (
            <button
              onClick={() => setCopilotOpen(true)}
              className="flex items-center gap-2 px-4 py-3 rounded-l-full font-semibold text-sm transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                backgroundColor: colors.primary,
                color: '#fff',
                boxShadow: `0 4px 20px ${colors.primary}60`,
              }}
            >
              <span>✨</span>
              <span>Ask AI</span>
            </button>
          ) : (
            <div
              className="rounded-l-2xl overflow-hidden"
              style={{
                backgroundColor: colors.cardBackground || colors.inputBackground,
                border: `1px solid ${colors.borderColor}`,
                borderRight: 'none',
                boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
                width: 340,
                maxHeight: '80vh',
              }}
            >
              <div className="p-4 overflow-y-auto" style={{ maxHeight: '80vh' }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span>✨</span>
                    <CText type="M14" color={colors.textColor}>Ask AI about {companyDisplayName}</CText>
                  </div>
                  <button
                    onClick={() => setCopilotOpen(false)}
                    className="p-1 rounded-full hover:opacity-70 transition-opacity"
                  >
                    {React.createElement(FiX as any, { size: 16, color: colors.grayScale400 })}
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 p-2.5 rounded-lg text-sm"
                    style={{
                      backgroundColor: colors.backgroundColor,
                      border: `1px solid ${colors.borderColor}`,
                      color: colors.textColor,
                    }}
                    placeholder="Type a question..."
                    value={copilotQuery}
                    onChange={(e) => setCopilotQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCopilotSend(copilotQuery)}
                    autoFocus
                  />
                  <button
                    className="p-2.5 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
                    style={{
                      backgroundColor: colors.primary,
                      color: '#fff',
                    }}
                    onClick={() => handleCopilotSend(copilotQuery)}
                  >
                    {React.createElement(FiSend as any, { size: 16 })}
                  </button>
                </div>
                <div className="mt-3">
                  <CText type="r12" color={colors.grayScale400} style={{ display: 'block', marginBottom: 8 }}>
                    💡 Try:
                  </CText>
                  <div className="flex flex-col gap-1.5">
                    {copilotNudges.map((nudge) => (
                      <button
                        key={nudge.label}
                        onClick={() => handleCopilotSend(nudge.query)}
                        className="text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2"
                        style={{
                          backgroundColor: colors.backgroundColor,
                          color: colors.textColor,
                          border: `1px solid ${colors.borderColor}`,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primary + '15')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundColor)}
                      >
                        <span>{nudge.icon}</span>
                        <span>{nudge.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyDetail;
